import type { RefObject } from "react";
import { useEffect, useRef, useState } from "react";

import { ACTIVE_LINE_FILL } from "./constants";

interface IUseLineMetricsProps {
    activeItem: number;
    totalItems: number;
}

interface ILineMetrics {
    wrapperRef: RefObject<HTMLDivElement>;
    lineHeight: number;
    activeFillPercentage: number;
}

export const useLineMetrics = ({ activeItem, totalItems }: IUseLineMetricsProps): ILineMetrics => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [lineHeight, setLineHeight] = useState<number>(0);
    const [activeFillPercentage, setActiveFillPercentage] = useState<number>(ACTIVE_LINE_FILL);

    useEffect(() => {
        if (!wrapperRef.current) {
            return;
        }

        const calculate = () => {
            const wrapper = wrapperRef.current;
            const itemWrappers = wrapper.querySelectorAll("[data-timeline-item]");

            if (itemWrappers.length === 0) {
                return;
            }

            const lastItemIndex = totalItems - 1;
            const lastItem = itemWrappers[lastItemIndex] as HTMLElement;

            let calculatedLineHeight = 0;

            if (lastItem) {
                calculatedLineHeight = lastItem.offsetTop;
                setLineHeight(calculatedLineHeight);
            }

            const activeItemIndex = activeItem - 1;

            if (activeItemIndex >= 0 && activeItemIndex < itemWrappers.length && calculatedLineHeight > 0) {
                const activeItemElement = itemWrappers[activeItemIndex] as HTMLElement;
                const activeItemTop = activeItemElement.offsetTop;

                const percentage = (activeItemTop / calculatedLineHeight) * 100;

                setActiveFillPercentage(Math.round(percentage));
            }
        };

        calculate();

        const observer = new ResizeObserver(calculate);
        observer.observe(wrapperRef.current);

        return () => observer.disconnect();
    }, [activeItem, totalItems]);

    return {
        wrapperRef,
        lineHeight,
        activeFillPercentage,
    };
};
